import * as React from 'react'
import {
  AbsenceComponentCode,
  AbsenceComponentModel,
  PayrollSettingsQueryModel,
} from '@/lib/api/company/configuration/payrollModule/types'
import { Table, TableRow, TableCell } from '@/ui/data-display/table-flex'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { Checkbox } from '@/ui'
import { defaultAbsenceComponentsArray, mergeAbsenceConfig, toAbsenceConfig } from './table-utils'

type Props = {
  state: PayrollSettingsQueryModel
  setState: React.Dispatch<React.SetStateAction<PayrollSettingsQueryModel>>
}

const getParsedAbsenceConfig = (state: PayrollSettingsQueryModel | null) => {
  if (state && Array.isArray(state.absenceComponents)) {
    const apiAbsenceConfig = toAbsenceConfig(state.absenceComponents)
    return mergeAbsenceConfig(apiAbsenceConfig)
  }
  return mergeAbsenceConfig({})
}

const isDisabled = (field: 'includeInLeave'|'isFixedComponent'|'includeInSick', abs: AbsenceComponentModel) => {
  switch(abs.code){
    case 'BASE':
      return true
    // case 'BONUS_A':
    case 'BONUS_B':
    case 'BONUS_C':
    case 'BONUS_D':
    case 'COST_C':
    case 'COST_D':
    case 'VACATION_PAY':
      return field === 'isFixedComponent' ? true : false
    default: 
      return false
  }
}

export const PayrollTable = ({ state, setState }: Props) => {
  const { t } = useLocalize()

  const absenceConfig = React.useMemo(() => {
    return Array.isArray(state.absenceComponents) ? state.absenceComponents : defaultAbsenceComponentsArray
  }, [state])

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value, name, checked } = e.currentTarget
      const config = getParsedAbsenceConfig(state)
      config[value][name] = checked
      const ac = Object.entries(config).map(([code, c]) => ({
        code,
        ...c,
      })) as AbsenceComponentModel[]
      setState((s) => ({
        ...s,
        absenceComponents: ac,
      }))
    },
    [setState, state],
  )

  return absenceConfig ? (
    <div>
      <Table>
        <TableRow className='tw-items-center'>
          <TableCell width={200} flex="none" className='tw-flex tw-items-center tw-justify-center tw-h-full tw-p-4 tw-text-center'> 
            {t('configuration.payrollModule.absenceComponents.componentName')}
          </TableCell>
          <TableCell flex={1} className='tw-flex tw-items-center tw-justify-center tw-h-full tw-p-4 tw-text-center'>
            {t('configuration.payrollModule.absenceComponents.includeInLeave')}
          </TableCell>
          <TableCell flex={1} className='tw-flex tw-items-center tw-justify-center tw-h-full tw-p-4 tw-text-center'>
            {t(
              'configuration.payrollModule.absenceComponents.isFixedComponent',
            )}
          </TableCell>
          <TableCell flex={1} className='tw-flex tw-items-center tw-justify-center tw-h-full tw-p-4 tw-text-center'>
            {t(
              'configuration.payrollModule.absenceComponents.includeInSickPay',
            )}
          </TableCell>
        </TableRow>
        {absenceConfig.map((ac, i) => {
          const { code } = ac
          return (
            <TableRow key={code} className='tw-items-center'>
              <TableCell width={200} flex="none" className='tw-flex tw-items-center tw-justify-start tw-h-full tw-p-2'>
                {t(
                  `configuration.payrollModule.absenceComponents.rows.${code}`,
                )}
              </TableCell>
              <TableCell flex={1} className='tw-flex tw-items-center tw-justify-center tw-h-full'>
                <Checkbox
                  value={code}
                  name="includeInLeave"
                  checked={code === 'BASE' ? true : ac.includeInLeave}
                  onChange={handleChange}
                  disabled={isDisabled('includeInLeave', ac)}
                />
              </TableCell>
              <TableCell flex={1} className='tw-flex tw-items-center tw-justify-center tw-h-full'>
                <Checkbox
                  value={code}
                  name="isFixedComponent"
                  checked={code === 'BASE' ? true : ac.isFixedComponent}
                  onChange={handleChange}
                  disabled={isDisabled('isFixedComponent', ac)}
                />
              </TableCell>
              <TableCell flex={1} className='tw-flex tw-items-center tw-justify-center tw-h-full'>
                <Checkbox
                  value={code}
                  name="includeInSick"
                  checked={code === 'BASE' ? true : ac.includeInSick}
                  onChange={handleChange}
                  disabled={isDisabled('includeInSick', ac)}
                />
              </TableCell>
            </TableRow>
          )
        })}
      </Table>
    </div>
  ) : null
}
